import {createSlice} from '@reduxjs/toolkit';
import isEqual from 'react-fast-compare';
import {FeelLike} from '../../api/types';
import {DateTimeConfig} from '../../utils/Dates';
import {Units} from '../../utils/Units';

/** `chain` is the multimodel segment chain; `single` is one model throughout. */
export type ForecastModelMode = 'single' | 'chain';

/**
 * `null` means "server default" (GFS-first), not "no opinion recorded".
 *
 * `national` was retired server-side: its ranking key was
 * `(regional-before-global, finest mesh)` with no notion of a country, so a
 * German station resolved to AROME, and it never differed from `resolution`
 * anywhere populated. The API now rejects it. A device that synced a value
 * before that deploy can still hold one, which is why `preferenceToFallback`
 * treats an unrecognised strategy as the server default rather than narrowing
 * on this union.
 */
export type ForecastModelStrategy = 'resolution' | 'ecmwf';

/**
 * The device-shared forecast model preference, as the backend models it since
 * #186: premium gate -> pins -> mode -> strategy. `order` is the legacy flat
 * drag-sort, which the backend still maintains as a mirror of pins/mode so a
 * pre-#186 install keeps working.
 */
export type ForecastPreference = {
  order: string[] | null;
  pins: string[] | null;
  mode: ForecastModelMode | null;
  strategy: ForecastModelStrategy | null;
};

export type ForecastPreferenceField = keyof ForecastPreference;

const PREFERENCE_FIELDS: ForecastPreferenceField[] = [
  'order',
  'pins',
  'mode',
  'strategy',
];

type SliceState = {
  language: string | null;
  units: Units | null;
  feelLike: FeelLike | null;
  date_time: DateTimeConfig | null;
  /**
   * Live despite no screen writing it: `editUserDetails` hydrates it from every
   * `/me` response, and `selectForecastModelOrder` feeds both
   * `getForecastModelsByGenericStationId` and
   * `getSelectedForecastModelByGenericStationId`, so it decides which model a
   * pre-#186 install resolves to. Dropping it would change that resolution —
   * it is not dead just because the app only ever writes pins/mode/strategy.
   */
  forecastModelOrder: string[] | null;
  forecastModelPins: string[] | null;
  forecastModelMode: ForecastModelMode | null;
  forecastModelStrategy: ForecastModelStrategy | null;
  /**
   * Which forecast-preference fields hold an edit no server has accepted.
   * Added to by every user edit, emptied by a successful PATCH of those
   * fields, and reset on logout (a pending edit belongs to the session that
   * made it — carrying it into the next login is how one account's order used
   * to land on another's profile).
   *
   * A field list rather than a boolean because the backend reads the payload
   * by KEY PRESENCE, not by value: `_sync_forecast_preference` translates a
   * legacy `forecast_model_order` write into pins/mode only when the payload
   * names none of the three new fields, and otherwise derives the order FROM
   * them. Sending the whole bag every time would make the legacy branch
   * unreachable and overwrite the user's drag-sort with an order derived from
   * their stale pins. Only what changed goes on the wire.
   *
   * It also replaces the old "the server sent an empty order" heuristic, which
   * could not tell a fresh anonymous edit from a copy left over by an earlier
   * session of the same account: a device offline through a change made
   * elsewhere pushed its stale list back up at next login.
   *
   * Existing installs upgrade to empty. That is the deliberate direction: an
   * order already on this device is treated as the server's until the user
   * touches it again. The narrow cost is a user who drag-sorted while signed
   * out before the upgrade and signs in for the first time after it — their
   * order yields to the server's rather than being pushed up. Seeding it
   * non-empty would reinstate exactly the stale overwrite it exists to
   * prevent.
   */
  forecastPreferencePending: ForecastPreferenceField[];
};

export const initialState: SliceState = {
  language: null,
  units: null,
  feelLike: null,
  date_time: null,
  forecastModelOrder: null,
  forecastModelPins: null,
  forecastModelMode: null,
  forecastModelStrategy: null,
  forecastPreferencePending: [],
};

const emptyToNull = (list: string[] | null | undefined) =>
  list && list.length > 0 ? list : null;

const valueOf = (state: SliceState, field: ForecastPreferenceField) => {
  switch (field) {
    case 'order':
      return state.forecastModelOrder;
    case 'pins':
      return state.forecastModelPins;
    case 'mode':
      return state.forecastModelMode;
    default:
      return state.forecastModelStrategy;
  }
};

const applyField = (
  state: SliceState,
  field: ForecastPreferenceField,
  payload: Partial<ForecastPreference>,
) => {
  switch (field) {
    case 'order':
      state.forecastModelOrder = emptyToNull(payload.order);
      break;
    case 'pins':
      state.forecastModelPins = emptyToNull(payload.pins);
      break;
    case 'mode':
      state.forecastModelMode = payload.mode ?? null;
      break;
    default:
      state.forecastModelStrategy = payload.strategy ?? null;
  }
};

// Defensive `|| []` everywhere this is read: the webapp's persistConfig
// misspells `stateReconciler`, so redux-persist falls back to autoMergeLevel1
// and rehydrates this slice wholesale from storage — an install that predates
// this field gets it back as undefined.
const pendingOf = (state: SliceState): ForecastPreferenceField[] =>
  state.forecastPreferencePending || [];

const markPending = (state: SliceState, fields: ForecastPreferenceField[]) => {
  const pending = pendingOf(state);
  state.forecastPreferencePending = [
    ...pending,
    ...fields.filter((field) => !pending.includes(field)),
  ];
};

export const userPreferenceSlice = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    storeLanguage: (state, {payload: languageCode}: {payload: string}) => {
      state.language = languageCode;
    },
    storeUnits: (state, {payload: units}: {payload: Units}) => {
      state.units = units;
    },
    storeFeelLike: (state, {payload: feelLike}: {payload: FeelLike}) => {
      state.feelLike = feelLike;
    },
    storeDateTime: (state, {payload: dateTime}: {payload: DateTimeConfig}) => {
      state.date_time = dateTime;
    },
    /**
     * A user edit of any subset of the #186 preference. Fields the payload
     * leaves out are untouched and stay unpending, so a screen can change the
     * mode without restating — or accidentally re-pushing — the pins.
     */
    storeForecastPreference: (
      state,
      {payload}: {payload: Partial<ForecastPreference>},
    ) => {
      const edited = PREFERENCE_FIELDS.filter((field) => field in payload);
      edited.forEach((field) => applyField(state, field, payload));
      markPending(state, edited);
    },
    /**
     * The server's value, from /me or a login response.
     *
     * Fields with a pending local edit are left alone; everything else takes
     * the server's answer. This is the whole cross-device pull, and doing it
     * per-field is what lets the caller hydrate unconditionally: an anonymous
     * drag-sort survives the login that follows it, a PATCH that failed is not
     * silently reverted by the next foreground refresh, and a value this
     * device has no claim on always yields.
     */
    hydrateForecastPreference: (
      state,
      {payload}: {payload: ForecastPreference},
    ) => {
      const pending = pendingOf(state);
      state.forecastPreferencePending = pending;
      PREFERENCE_FIELDS.filter((field) => !pending.includes(field)).forEach(
        (field) => applyField(state, field, payload),
      );
    },
    /**
     * A PATCH carrying `payload` was accepted; drop the fields it settles.
     *
     * A field is only cleared if the store still holds the value that went out
     * — an in-flight push for an older snapshot must not settle a newer,
     * unsent edit, which would then never be retried and would be lost at the
     * next hydration.
     *
     * Acknowledging `order` additionally settles pins/mode/strategy: the
     * backend rewrites those from the order it just accepted, so a follow-up
     * PATCH of the pre-existing pins would undo the order that was the user's
     * actual intent.
     */
    markForecastPreferenceSynced: (
      state,
      {payload}: {payload: Partial<ForecastPreference>},
    ) => {
      const acknowledged = PREFERENCE_FIELDS.filter(
        (field) =>
          field in payload && isEqual(valueOf(state, field), payload[field]),
      );
      const settled = acknowledged.includes('order')
        ? PREFERENCE_FIELDS
        : acknowledged;
      state.forecastPreferencePending = pendingOf(state).filter(
        (field) => !settled.includes(field),
      );
    },
  },
  extraReducers: (builder) => {
    // The root reducers of both apps keep `userPreference` across LOGOUT so an
    // anonymous session inherits the user's settings. The pending list must
    // not come with it: it describes a write owed by the session that just
    // ended, and carrying it forward is what let account A's order be pushed
    // onto account B's profile at the next login.
    builder.addCase('LOGOUT', (state) => {
      state.forecastPreferencePending = [];
    });
  },
});

export const {
  storeLanguage,
  storeUnits,
  storeFeelLike,
  storeDateTime,
  storeForecastPreference,
  hydrateForecastPreference,
  markForecastPreferenceSynced,
} = userPreferenceSlice.actions;

export default userPreferenceSlice.reducer;
